import React, { useState, useEffect } from 'react';
import { Clock, Save, Plus, Trash2, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const DAYS = [
  { id: 0, name: 'Lundi' },
  { id: 1, name: 'Mardi' },
  { id: 2, name: 'Mercredi' },
  { id: 3, name: 'Jeudi' },
  { id: 4, name: 'Vendredi' },
  { id: 5, name: 'Samedi' },
  { id: 6, name: 'Dimanche' }
];

const TIME_OPTIONS = [];
for (let h = 6; h <= 21; h++) {
  for (let m = 0; m < 60; m += 30) {
    const hour = h.toString().padStart(2, '0');
    const minute = m.toString().padStart(2, '0');
    TIME_OPTIONS.push(`${hour}:${minute}`);
  }
}

const DoctorScheduleManager = () => {
  const [schedule, setSchedule] = useState(
    DAYS.map(day => ({
      day: day.id,
      is_available: false,
      slots: []
    }))
  );
  const [consultationDuration, setConsultationDuration] = useState(30);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSchedule();
  }, []);

  const fetchSchedule = async () => {
    try {
      const response = await axios.get(`${API}/doctors/schedule/me`);
      if (response.data.weekly_schedule) {
        setSchedule(response.data.weekly_schedule);
      }
      if (response.data.consultation_duration) {
        setConsultationDuration(response.data.consultation_duration);
      }
    } catch (error) {
      console.error('Failed to fetch schedule', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleDayAvailability = (dayId) => {
    setSchedule(prev => prev.map(day => {
      if (day.day === dayId) {
        return {
          ...day,
          is_available: !day.is_available,
          slots: !day.is_available && day.slots.length === 0 
            ? [{ start: '08:00', end: '12:00' }, { start: '14:00', end: '18:00' }]
            : day.slots
        };
      }
      return day;
    }));
  };

  const addTimeSlot = (dayId) => {
    setSchedule(prev => prev.map(day => {
      if (day.day === dayId) {
        return {
          ...day,
          slots: [...day.slots, { start: '09:00', end: '12:00' }]
        };
      }
      return day;
    }));
  };

  const removeTimeSlot = (dayId, slotIndex) => {
    setSchedule(prev => prev.map(day => {
      if (day.day === dayId) {
        return {
          ...day,
          slots: day.slots.filter((_, idx) => idx !== slotIndex)
        };
      }
      return day;
    }));
  };

  const updateTimeSlot = (dayId, slotIndex, field, value) => {
    setSchedule(prev => prev.map(day => {
      if (day.day === dayId) {
        const newSlots = [...day.slots];
        newSlots[slotIndex] = { ...newSlots[slotIndex], [field]: value };
        return { ...day, slots: newSlots };
      }
      return day;
    }));
  };

  const saveSchedule = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/doctors/schedule`, {
        weekly_schedule: schedule,
        consultation_duration: consultationDuration
      });
      toast.success('Emploi du temps enregistré avec succès !');
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
      console.error('Failed to save schedule', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="schedule-manager">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Gérer mes Disponibilités</h2>
          <p className="text-sm text-gray-600">Définissez vos jours et heures de consultation</p>
        </div>
        <Button onClick={saveSchedule} disabled={saving} className="btn-primary" data-testid="save-schedule-btn">
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              Enregistrement...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Enregistrer
            </>
          )}
        </Button>
      </div>

      {/* Consultation Duration */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center space-x-3">
              <Clock className="w-5 h-5 text-teal-600" />
              <div>
                <p className="font-medium">Durée d'une consultation</p>
                <p className="text-sm text-gray-500">Temps alloué par patient</p>
              </div>
            </div>
            <select
              value={consultationDuration}
              onChange={(e) => setConsultationDuration(parseInt(e.target.value))}
              className="border rounded-lg px-4 py-2 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              data-testid="duration-select"
            >
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={45}>45 minutes</option>
              <option value={60}>1 heure</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Weekly Schedule */}
      <div className="space-y-4">
        {DAYS.map(day => {
          const daySchedule = schedule.find(s => s.day === day.id) || { is_available: false, slots: [] };
          
          return (
            <Card key={day.id} className={`transition-all ${daySchedule.is_available ? 'border-teal-300 bg-teal-50/30' : ''}`}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <Switch
                      checked={daySchedule.is_available}
                      onCheckedChange={() => toggleDayAvailability(day.id)}
                      data-testid={`switch-${day.name.toLowerCase()}`}
                    />
                    <span className={`font-semibold ${daySchedule.is_available ? 'text-teal-700' : 'text-gray-500'}`}>
                      {day.name}
                    </span>
                    {daySchedule.is_available && (
                      <span className="text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded-full flex items-center">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Disponible
                      </span>
                    )}
                  </div>
                  {daySchedule.is_available && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addTimeSlot(day.id)}
                      className="text-teal-600 border-teal-300 hover:bg-teal-50"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Ajouter un créneau
                    </Button>
                  )}
                </div>

                {daySchedule.is_available && daySchedule.slots.length > 0 && (
                  <div className="space-y-3 pl-12">
                    {daySchedule.slots.map((slot, idx) => (
                      <div key={idx} className="flex items-center space-x-3 bg-white p-3 rounded-lg border">
                        <span className="text-sm text-gray-600 w-8">De</span>
                        <select
                          value={slot.start}
                          onChange={(e) => updateTimeSlot(day.id, idx, 'start', e.target.value)}
                          className="border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500"
                        >
                          {TIME_OPTIONS.map(time => (
                            <option key={time} value={time}>{time}</option>
                          ))}
                        </select>
                        <span className="text-sm text-gray-600 w-4">à</span>
                        <select
                          value={slot.end}
                          onChange={(e) => updateTimeSlot(day.id, idx, 'end', e.target.value)}
                          className="border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500"
                        >
                          {TIME_OPTIONS.map(time => (
                            <option key={time} value={time}>{time}</option>
                          ))}
                        </select>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeTimeSlot(day.id, idx)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {daySchedule.is_available && daySchedule.slots.length === 0 && (
                  <p className="text-sm text-gray-500 pl-12">
                    Aucun créneau défini. Cliquez sur "Ajouter un créneau" pour commencer.
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default DoctorScheduleManager;
